import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';

import { Observable } from 'rxjs';

import { ActionCommentRequestBody, ActionResponseBody, ActionType, EntityType } from '@lib/core/models/action.interface';
import {
  AppointmentSuggestionResponse,
  LaboratoryScheduleSearch,
  LaboratoryWithScheduleResponse,
  PlanPrescriptionPayload,
  UpdatePrescriptionPlanningPayload,
} from '@lib/core/models/create-appointment.interface';
import { PagedResponse } from '@lib/core/models/paged-response';
import { AppointmentStatus } from '@lib/core/models/visit.interface';

import { PrescriptionPlanningStatus } from '../models/prescription.interface';
import { API_URL } from '@lib/core/tokens/api.tokens';

export interface PlanningDetailsResponse {
  planningId: string;
  appointmentId: string | null;
  status: PrescriptionPlanningStatus;
  statusReason: string | null;
  doctorName: string | null;
  appointmentTime: string | null;
  createdAt: string;
  serviceName: string;
  serviceType: string;
  partnerId: string | null;
  partnerName: string | null;
  locationId: string;
  locationAddress: string;
  locationName: string | null;
  locationCity: string | null;
  locationPhoneNumber: string | null;
  locationEmail: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AppointmentsHttpService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(API_URL);

  createAppointment(payload: PlanPrescriptionPayload) {
    return this.http.post<string>(`${this.apiUrl}/prescriptions/plannings`, payload);
  }

  cancelAppointment(prescriptionId: string, planningId: string) {
    return this.http.post(`${this.apiUrl}/prescriptions/${prescriptionId}/plannings/${planningId}/cancel`, {});
  }

  updateAppointment(prescriptionId: string, planningId: string, payload: UpdatePrescriptionPlanningPayload) {
    return this.http.post(`${this.apiUrl}/prescriptions/${prescriptionId}/plannings/${planningId}/update`, payload);
  }

  updateAppointmentStatus(appointmentId: string, status: AppointmentStatus): Observable<unknown> {
    return this.http.put<unknown>(`${this.apiUrl}/members/appointments/${appointmentId}/status`, { status });
  }

  getPlanningDetails(prescriptionId: string, planningId: string): Observable<PlanningDetailsResponse> {
    return this.http.get<PlanningDetailsResponse>(
      `${this.apiUrl}/prescriptions/${prescriptionId}/plannings/${planningId}/details`
    );
  }

  getAppointmentSuggestions(prescriptionId: string, userId: string, partnerId: string) {
    const options = {
      params: new HttpParams().set('partnerId', partnerId).set('userId', userId),
    };

    return this.http.get<AppointmentSuggestionResponse[]>(`${this.apiUrl}/prescriptions/${prescriptionId}/suggestions`, options);
  }

  searchLaboratoriesSchedule(payload: LaboratoryScheduleSearch) {
    return this.http.post<LaboratoryWithScheduleResponse[]>(`${this.apiUrl}/laboratories/schedule`, payload);
  }

  getAppointmentNote(entityId: string): Observable<ActionResponseBody> {
    return this.http.get<ActionResponseBody>(`${this.apiUrl}/notes/notes/${entityId}`);
  }

  searchAppointmentNote(appointmentId: string): Observable<PagedResponse<ActionResponseBody>> {
    const payload = {
      pageSize: 10,
      sortBy: 'CreatedAt',
      sortDirection: 'desc',
      entityId: appointmentId,
      entityType: EntityType.Appointment,
      type: ActionType.AppointmentNote,
    };

    return this.http.post<PagedResponse<ActionResponseBody>>(`${this.apiUrl}/notes/search`, payload);
  }

  createAppointmentNote(action: ActionCommentRequestBody): Observable<ActionResponseBody> {
    return this.http.post<ActionResponseBody>(`${this.apiUrl}/notes`, action);
  }

  updateAppointmentNote(actionId: string, actionContent: string): Observable<ActionResponseBody> {
    return this.http.patch<ActionResponseBody>(`${this.apiUrl}/notes/${actionId}`, { content: actionContent });
  }
}
